/**
 * The Clinvisor wordmark.
 *
 * One component, used everywhere the product names itself, so the mark cannot
 * drift between the login screen, the sidebar and the public booking page the
 * way three hand-written copies would.
 *
 * The ring **is** the "o" in Clinvis-o-r. It is not decoration beside the word:
 * `<span>Clinvis</span><ring/><span>r</span>` is the mark, and that is why the
 * word is split rather than written out with an icon in front. The polyline
 * through it is an ECG trace.
 *
 * Transcribed from the marketing site (`landing-page/src/App.tsx`), which is
 * where the mark was designed. The geometry — `viewBox 0 0 40 40`, r=16, the
 * dash array that opens the ring on the right — is copied exactly rather than
 * approximated, because two nearly-identical rings look like a mistake.
 */
import { color } from './tokens';

export interface BrandMarkProps {
  /** Font size of the wordmark. The ring scales with it, as on the site. */
  size?: string;
  /** Wordmark colour. The ring keeps its own accent unless `ringColor` says otherwise. */
  textColor?: string;
  /** Defaults to the brand accent, which reads on both the navy and white surfaces. */
  ringColor?: string;
  weight?: number;
  /** Renders the ring alone — for a favicon-sized slot or a collapsed sidebar. */
  markOnly?: boolean;
}

/**
 * The ring on its own.
 *
 * `aria-hidden`: it is a glyph inside a word, and a screen reader announcing
 * "image" between "Clinvis" and "r" would break the name into three pieces.
 * The accessible name comes from the wrapper below.
 */
export function BrandRing({
  size = '1em',
  ringColor = color.brand.primary,
}: {
  size?: string;
  ringColor?: string;
}) {
  return (
    <svg
      viewBox="0 0 40 40"
      aria-hidden="true"
      focusable="false"
      style={{
        width: `calc(${size} * 0.643)`,
        height: `calc(${size} * 0.643)`,
        flex: 'none',
        margin: '0 1px',
        // The ring sits fractionally low so its centre lines up with the
        // x-height of the letters either side rather than with their baseline.
        transform: 'translateY(0.036em)',
      }}
    >
      <circle
        cx="20"
        cy="20"
        r="16"
        fill="none"
        stroke={ringColor}
        strokeWidth="6"
        strokeDasharray="37.27 13"
        strokeDashoffset="43.77"
      />
      <polyline
        points="5,20 14,20 18,12 22,28 26,20 35,20"
        fill="none"
        stroke={ringColor}
        strokeWidth="4.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BrandMark({
  size = '1.25rem',
  textColor = 'inherit',
  ringColor = color.brand.primary,
  weight = 800,
  markOnly = false,
}: BrandMarkProps) {
  if (markOnly) {
    return (
      <span role="img" aria-label="Clinvisor" style={{ display: 'inline-flex' }}>
        <BrandRing size={size} ringColor={ringColor} />
      </span>
    );
  }

  return (
    // One accessible name for the whole lockup. Without this the mark reads as
    // "Clinvis r" — the ring is the letter the name is missing.
    <span
      role="img"
      aria-label="Clinvisor"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: size,
        fontWeight: weight,
        color: textColor,
        // The name is Latin in an otherwise RTL interface; forcing the
        // direction stops the ring being reordered into the wrong side of the
        // word inside an Arabic paragraph.
        direction: 'ltr',
        unicodeBidi: 'isolate',
        letterSpacing: '-0.01em',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <span>Clinvis</span>
      <BrandRing size={size} ringColor={ringColor} />
      <span>r</span>
    </span>
  );
}
